import React, { useContext, useEffect, useState, useTransition } from 'react';
import { useLocation, useRoutes } from 'react-router-dom';
import { routes } from '../routes';
import { ProtectedPage } from '../pages/ProtectedPage';
import { AdsContext } from '../ads';
import { settings } from '../../settings';

export const selectedIdParam = 'selectedId';
export const isItemPath = (path) => path.includes('/item/');
export const isPlaylistPath = (path) => path.includes('/afspeellijst/');
export const RouterContext = React.createContext({});
RouterContext.displayName = 'Router';

let playerPreloaded = false;
const preloadPlayer = () => {
  if (playerPreloaded) {
    return;
  }
  const host = new URL(
    settings.PLAYER_SCRIPT_2.startsWith('//')
      ? `https:${settings.PLAYER_SCRIPT_2}`
      : settings.PLAYER_SCRIPT_2,
  ).host;
  const node = document.getElementsByTagName('head')[0];
  const createLink = (type) => (href) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = type;
    link.href = `https://${host}${href}`;
    if (type === 'font') {
      link.type = 'font/woff';
      link.crossOrigin = 'anonymous';
    }
    return link;
  };

  [
    '/static/agnoplay/js/agnoplay.js',
    '/static/agnoplay/js/moatChunk.js',
    '/static/agnoplayer/agnoplayer.js',
    '/static/agnoplayer/vendor/video.js/dist/video.min.js',
    '/static/agnoplayer/vendor/mux.js/dist/mux.min.js',
    '/static/agnoplayer/vendor/shaka-player/dist/shaka-player.compiled.js',
  ]
    .map(createLink('script'))
    .concat(
      [
        '/static/agnoplayer/agnoplayer.css',
        '/static/static/agnoplayer/vendor/video.js/dist/video-js.min.css',
      ].map(createLink('style')),
    )
    .concat(
      [
        '/static/agnoplayer/fonts/Roboto-Regular.woff',
        '/static/agnoplayer/fonts/Roboto-Bold.woff',
      ].map(createLink('font')),
    )
    .forEach((link) => node.appendChild(link));
  playerPreloaded = true;
};

const parsePathname = (pathname, tag) => {
  const pathParts = pathname.split('/');
  const page = pathParts[1] || 'nieuw';
  const isFloppers = page === 'floppers';
  const isToppers = page === 'toppers';
  const isDumpertTv = page === 'dumperttv';
  const isDate = page === 'datum';
  const isSearch = page === 'zoek';
  const isHistory = page === 'nieuw' && pathParts[2] && ['dag', 'week'].includes(pathParts[2]);
  const listType =
    tag || isDumpertTv
      ? isSearch
        ? 'search'
        : 'tag'
      : isToppers
        ? 'toppers'
        : isFloppers
          ? 'floppers'
          : 'latest';
  const activeTag = isDumpertTv ? 'dumperttv' : tag;
  const activeList = activeTag || page;
  const pageTitle =
    tag || isDumpertTv
      ? isDumpertTv
        ? 'DumpertTV'
        : `${isSearch ? 'Zoek:' : 'Tag:'} ${tag}`
      : isToppers
        ? 'Toppers'
        : isFloppers
          ? 'Floppers'
          : 'nieuw';
  return {
    page,
    listType,
    isFloppers,
    isToppers,
    isDumpertTv,
    isDate,
    isSearch,
    isHistory,
    activeTag,
    activeList,
    pageTitle,
  };
};

export const RouterContextProvider = ({ children }) => {
  const location = useLocation();
  const { pathname, search } = location;

  const { dispatchKachingConfig } = useContext(AdsContext);

  const routeMatch = useRoutes(
    routes.map(({ path, PageComponent, protectedPage, adunit }) => ({
      path,
      element: protectedPage ? (
        <ProtectedPage pathname={pathname}>
          <PageComponent />
        </ProtectedPage>
      ) : (
        <PageComponent />
      ),
      adunit,
    })),
  );
  const adunit = routeMatch.props.match.route.adunit;
  const params = routeMatch.props.match.params;

  const isModalActive = new URLSearchParams(search).has(selectedIdParam);
  const currentSearch = new URLSearchParams(search);
  const isCurrentItemPopup = currentSearch.has(selectedIdParam);
  const isCurrentItem = isCurrentItemPopup || isItemPath(pathname);
  const isPlaylistItem = isPlaylistPath(pathname);
  const currentKachingConfig =
    isCurrentItem || isPlaylistItem
      ? {
          type: isPlaylistItem ? 'playlist' : 'item',
          payload: {
            id: new URLSearchParams(search).get(selectedIdParam) || params.id,
            adunit: 'detail',
          },
        }
      : { type: adunit || 'homepage', payload: { adunit } };

  const [kachingConfig, setKachingConfig] = useState(currentKachingConfig);

  const [route, setRoute] = useState({
    to: pathname + search,
    from: undefined,
    routeMatch,
    search,
    isModalActive,
    isDetailPage: isModalActive || isItemPath(pathname),
    isItemDirect: isItemPath(pathname),
    pagePath: parsePathname(pathname, params.tag),
  });

  const setTransition = useTransition()[1];

  useEffect(() => {
    setTransition(() => {
      dispatchKachingConfig(kachingConfig);
    });
  }, [kachingConfig]);

  useEffect(() => {
    setTransition(() => {
      setRoute((prev) => {
        const prevSearch = prev.to ? new URL(prev.to, 'https://www.dumpert.nl').searchParams : null;
        const to = pathname + search;

        if (prev.to !== to || prev.routeMatch.props.match.route.adunit !== adunit) {
          setKachingConfig(currentKachingConfig);
        }

        return prev.to !== to
          ? {
              to,
              from: prev.to,
              routeMatch,
              isPlaylistItem,
              isModalActive: isCurrentItemPopup,
              isDetailPage: isCurrentItem,
              isItemDirect: isItemPath(pathname),
              search: currentSearch.toString(),
              pagePath: parsePathname(pathname, params.tag),
              isPrevItem:
                (prevSearch && prevSearch.has(selectedIdParam)) || (prev.to && isItemPath(prev.to)),
            }
          : prev;
      });
    });

    // preload on any page
    preloadPlayer();
  }, [location]);

  return <RouterContext.Provider value={route}>{children}</RouterContext.Provider>;
};
